import { Button, Form, Input, message } from "antd";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  passkeySignIn,
  respondToSignIn,
  selectMFASetupQrcode,
  selectSessionToken,
  selectUserAccessLevel,
  signIn,
} from "./accountSlice";
import { isWebAuthnSupported } from "./webauthn";

export default function AccessControl() {
  const session = useAppSelector(selectSessionToken);
  const userAccessLevel = useAppSelector(selectUserAccessLevel);
  const qrcode = useAppSelector(selectMFASetupQrcode);
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<"email" | "password">("email");
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [confirmCodeLoading, setConfirmCodeLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [confirmForm] = Form.useForm();
  const otpRef = useRef<any>(null);

  useEffect(() => {
    const refreshToken = sessionStorage.getItem("refreshToken");
    if ((userAccessLevel && userAccessLevel.length > 0) || refreshToken)
      navigate("/home");

    return () => {};
  }, [userAccessLevel, qrcode]);

  const onEmailContinue = (values: any) => {
    const nextEmail = values.email;
    setEmail(nextEmail);
    if (!isWebAuthnSupported()) {
      setPhase("password");
      return;
    }
    setPasskeyLoading(true);
    dispatch(passkeySignIn({ email: nextEmail }))
      .then((result) => {
        const outcome = (result?.payload as any)?.outcome;
        if (outcome === "error")
          message.error("Passkey sign-in failed. Please use your password.");
        if (outcome !== "success") setPhase("password");
      })
      .catch(() => setPhase("password"))
      .finally(() => setPasskeyLoading(false));
  };

  const onPasswordFinish = (values: any) => {
    const nextEmail = values.email || email;
    const { password } = values;
    setEmail(nextEmail);
    setSignInLoading(true);
    dispatch(signIn({ email: nextEmail, password }))
      .then((result) => {
        if ((result?.payload as any)?.qrcode) navigate("/mfaSetup");
      })
      .finally(() => setSignInLoading(false));
  };

  const onConfirmationCodeFinish = (values: any) => {
    const { confirmationCode } = values;
    if (session) {
      setConfirmCodeLoading(true);
      dispatch(
        respondToSignIn({ confirmationCode, session, username: email })
      ).finally(() => {
        setConfirmCodeLoading(false);
        confirmForm.resetFields(["confirmationCode"]);
        setTimeout(() => {
          otpRef.current?.focus();
        }, 100);
      });
    }
  };

  const handleOTPChange = (value: string) => {
    if (value && value.length === 6) {
      confirmForm.submit();
    }
  };

  useEffect(() => {
    if (session && !qrcode && otpRef.current) {
      setTimeout(() => {
        otpRef.current?.focus();
      }, 100);
    }
  }, [session, qrcode]);

  return (
    <div>
      {!session && phase === "email" && (
        <Form
          name="passkey-email"
          layout="vertical"
          onFinish={onEmailContinue}
          autoComplete="on"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, message: "Please input your email!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={passkeyLoading}
              block
            >
              Continue
            </Button>
          </Form.Item>
          <Button type="link" onClick={() => setPhase("password")} block>
            Sign in with password
          </Button>
        </Form>
      )}

      {!session && phase === "password" && (
        <Form
          name="password-signin"
          layout="vertical"
          initialValues={{ email }}
          onFinish={onPasswordFinish}
          autoComplete="on"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, message: "Please input your email!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={signInLoading}
              block
            >
              Sign in
            </Button>
          </Form.Item>
          <Button type="link" onClick={() => setPhase("email")} block>
            Back
          </Button>
        </Form>
      )}

      {session && !qrcode && (
        <Form form={confirmForm} onFinish={onConfirmationCodeFinish}>
          <Form.Item
            label="Confirmation Code"
            name="confirmationCode"
            tooltip="Please enter the code showing up in authenticator app"
            rules={[
              {
                required: true,
                message: "Please enter your confirmationCode!",
              },
            ]}
          >
            <Input.OTP
              ref={otpRef}
              formatter={(str) => str.toUpperCase()}
              onChange={handleOTPChange}
            />
          </Form.Item>
          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={confirmCodeLoading}
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  );
}
